import { useState, useMemo } from 'react';
import { Filter, CheckCircle2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';

interface MemoTypeFilterProps {
    selectedType: string | null;
    onTypeChange: (type: string | null) => void;
    selectedEntity?: string | null;
    onEntityChange?: (entity: string | null) => void;
    disabled?: boolean;
}

const MEMO_TYPES = [
    { value: 'TODO', label: 'TODO', color: 'text-orange-600 dark:text-orange-400' },
    { value: 'RESOURCE', label: 'RESOURCE', color: 'text-blue-600 dark:text-blue-400' },
    { value: 'QUESTION', label: 'QUESTION', color: 'text-purple-600 dark:text-purple-400' },
    { value: 'IMPULSE', label: 'IMPULSE', color: 'text-pink-600 dark:text-pink-400' },
];

export function MemoTypeFilter({
    selectedType,
    onTypeChange,
    selectedEntity,
    onEntityChange,
    disabled
}: MemoTypeFilterProps) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [entitySearch, setEntitySearch] = useState('');

    const { data: entitiesData } = useQuery({
        queryKey: ['entities'],
        queryFn: () => api.getEntities(),
        staleTime: 5 * 60 * 1000,
    });

    const entities = entitiesData?.data || [];

    const filteredEntities = useMemo(() => {
        let result = entities;
        if (entitySearch) {
            const lowerSearch = entitySearch.toLowerCase();
            result = entities.filter((e: any) =>
                e.code.toLowerCase().includes(lowerSearch)
            );
        }
        // Sort by entity code in ascending order
        return result.sort((a: any, b: any) => a.code.localeCompare(b.code));
    }, [entities, entitySearch]);

    const handleTypeSelect = (type: string) => {
        if (selectedType === type) {
            onTypeChange(null);
        } else {
            onTypeChange(type);
        }
        setIsPopoverOpen(false);
    };

    const handleEntitySelect = (entityCode: string) => {
        if (!onEntityChange) return;
        // Toggle selection
        if (selectedEntity === entityCode) {
            onEntityChange(null);
        } else {
            onEntityChange(entityCode);
        }
        setIsPopoverOpen(false);
    };

    const clearFilters = () => {
        onTypeChange(null);
        if (onEntityChange) onEntityChange(null);
        setIsPopoverOpen(false);
    }

    const isActive = selectedType !== null || (selectedEntity !== null && selectedEntity !== undefined);

    return (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    className={cn(
                        "h-12 w-12 sm:h-14 sm:w-14 rounded-full transition-all",
                        "bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md",
                        "shadow-lg shadow-zinc-900/10 dark:shadow-zinc-950/50",
                        "border border-zinc-200/50 dark:border-zinc-800/50",
                        "hover:shadow-xl hover:shadow-zinc-900/15 dark:hover:shadow-zinc-950/60",
                        "hover:scale-105",
                        isActive && "bg-primary/10 dark:bg-primary/20 border-primary/50"
                    )}
                    aria-label="Filter"
                >
                    <Filter className={cn(
                        "h-5 w-5 sm:h-6 sm:w-6 transition-colors",
                        isActive && "text-primary"
                    )} />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-64 p-2"
                align="center"
                side="top"
                sideOffset={12}
            >
                <div className="space-y-2">
                    <div className="flex items-center justify-between px-2 py-1.5">
                        <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">
                            Filter Memos
                        </span>
                        {isActive && (
                            <button
                                onClick={clearFilters}
                                className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                            >
                                Clear all
                            </button>
                        )}
                    </div>

                    {/* Memo Types */}
                    <div className="space-y-1">
                        {MEMO_TYPES.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => handleTypeSelect(type.value)}
                                className={cn(
                                    "w-full flex items-center justify-between px-3 py-2 rounded-md",
                                    "text-sm font-medium transition-colors",
                                    "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                                    selectedType === type.value
                                        ? "bg-zinc-100 dark:bg-zinc-800"
                                        : "bg-transparent"
                                )}
                            >
                                <span className={type.color}>{type.label}</span>
                                {selectedType === type.value && (
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                )}
                            </button>
                        ))}
                    </div>

                    {onEntityChange && (
                        <>
                            <div className="h-px bg-zinc-200 dark:bg-zinc-800 mx-2 my-2" />

                            {/* Entity Filter */}
                            <div className="space-y-2">
                                <div className="px-2">
                                    <div className="relative">
                                        <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-zinc-500" />
                                        <Input
                                            placeholder="Filter by entity..."
                                            value={entitySearch}
                                            onChange={(e) => setEntitySearch(e.target.value)}
                                            className="h-8 pl-8 text-xs bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus-visible:ring-1"
                                        />
                                        {entitySearch && (
                                            <button
                                                onClick={() => setEntitySearch('')}
                                                className="absolute right-2 top-2.5 text-zinc-400 hover:text-zinc-600"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="max-h-48 overflow-y-auto px-1 space-y-0.5 scrollbar-thin scrollbar-thumb-zinc-200 dark:scrollbar-thumb-zinc-800">
                                    {filteredEntities.length === 0 ? (
                                        <div className="px-3 py-2 text-xs text-zinc-500 text-center">
                                            No entities found
                                        </div>
                                    ) : (
                                        filteredEntities.map((entity: any) => (
                                            <button
                                                key={entity.id || entity.code}
                                                onClick={() => handleEntitySelect(entity.code)}
                                                className={cn(
                                                    "w-full flex items-center justify-between px-3 py-1.5 rounded-md",
                                                    "text-sm transition-colors text-left",
                                                    "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                                                    selectedEntity === entity.code
                                                        ? "bg-zinc-100 dark:bg-zinc-800 text-primary font-medium"
                                                        : "text-zinc-600 dark:text-zinc-400"
                                                )}
                                            >
                                                <span className="truncate">#{entity.code}</span>
                                                {selectedEntity === entity.code && (
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
